import clsx from 'clsx';
import {Image, Link, Money, useMoney} from '@shopify/hydrogen';

import {Text} from '~/components';
import {isDiscounted, isNewArrival, resolvePath} from '~/lib/utils';
import {CurrencyCode, MoneyV2} from '@shopify/hydrogen/storefront-api-types';
import {ProductHit} from '~/types/algoliaTypes';

export function AlgoliaProductCard({
  product,
  label,
  className,
  loading,
  showVariantTitle = true,
  onClick,
}: {
  product: ProductHit;
  label?: string;
  className?: string;
  loading?: HTMLImageElement['loading'];
  showVariantTitle?: boolean;
  onClick?: () => void;
}) {
  let cardLabel;

  const {image, price: price_plain, compare_at_price} = product;

  const price: MoneyV2 = {
    amount: '' + price_plain, // Convert to string
    currencyCode: CurrencyCode.Eur,
  };

  const compareAtPrice: MoneyV2 = {
    amount: '' + compare_at_price, // Convert to string
    currencyCode: CurrencyCode.Eur,
  };

  if (label) {
    cardLabel = label;
  } else if (compare_at_price > price_plain) {
    cardLabel = 'Ale';
  } else if (isNewArrival(product.published_at)) {
    cardLabel = 'Uusi';
  }

  const variantTitle = product.variant_title
    ? 'Default Title' == product.variant_title
      ? ''
      : product.variant_title
    : '';

  const styles = clsx('grid gap-2 product-card', className);

  return (
    <Link
      onClick={onClick}
      to={resolvePath('product', product.handle)}
      className={styles}
    >
      <div className="card-image aspect-[4/5] bg-primary/5">
        <Text
          as="label"
          size="fine"
          className="z-20 absolute w-full tracking-widest font-clash text-base lg:text-lg bottom-0 font-semibold bg-accentDarkRed text-white uppercase right-0 text-center text-notice"
        >
          {cardLabel}
        </Text>
        {image && (
          <>
            <Image
              className="aspect-[4/5] w-full object-cover fadeIn z-20"
              sizes="320px"
              width={320}
              height={400}
              loaderOptions={{
                scale: 2,
                width: 320,
              }}
              src={image}
              alt={`Picture of ${product.title}`}
              loading={loading}
            />
            <div className="overlay z-25"></div>
          </>
        )}
      </div>
      <div className="grid gap-1">
        {product.vendor && (
          <div className="mb-1 text-xxs tracking-wider font-semibold font-clash uppercase text-ellipsis whitespace-nowrap overflow-hidden">
            {product.vendor}
          </div>
        )}
        <Text
          className="w-full mb-3 font-inter normal-case overflow-hidden font-normal text-sm mt-0"
          as="h3"
        >
          {product.title}
        </Text>
        {variantTitle && showVariantTitle && (
          <Text className="text-xs text-textTertiary">
            {product.variant_title}
          </Text>
        )}
        <Text className="flex tracking-wider font-clash items-center gap-2 text-sm lg:text-lg">
          <Money withoutTrailingZeros data={price!} />
          {isDiscounted(price as MoneyV2, compareAtPrice as MoneyV2) && (
            <CompareAtPrice
              className={'opacity-50 text-sm'}
              data={compareAtPrice as MoneyV2}
            />
          )}
        </Text>
      </div>
    </Link>
  );
}

function CompareAtPrice({
  data,
  className,
}: {
  data: MoneyV2;
  className?: string;
}) {
  const {currencyNarrowSymbol, withoutTrailingZerosAndCurrency} =
    useMoney(data);

  const styles = clsx('line-through', className);

  return (
    <span className={styles}>
      {currencyNarrowSymbol}
      {withoutTrailingZerosAndCurrency}
    </span>
  );
}
